import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { LoadingFallback } from "@/components/ui/LoadingFallback";
import CookieBanner from "./components/CookieBanner";

// Eagerly load Home for fast LCP
import Home from "./pages/Home";

// Lazy load all other pages
const AIMasteryProgram = lazy(() => import("./pages/AIMasteryProgram"));
const GDPR = lazy(() => import("./pages/GDPR"));
const VOP = lazy(() => import("./pages/VOP"));
const Materialy = lazy(() => import("./pages/Materialy"));
const ZadarmoPage = lazy(() => import("./pages/ZadarmoPage"));
const AITest = lazy(() => import("./pages/AITest"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const CRMDashboard = lazy(() => import("./pages/admin/CRMDashboard"));
const DashboardHome = lazy(() => import("./pages/admin/DashboardHome"));
const PipelineView = lazy(() => import("./pages/admin/PipelineView"));
const OrganizationsPage = lazy(() => import("./pages/admin/OrganizationsPage"));
const ContactsPage = lazy(() => import("./pages/admin/ContactsPage"));
const OpportunitiesPage = lazy(() => import("./pages/admin/OpportunitiesPage"));
const TasksPage = lazy(() => import("./pages/admin/TasksPage"));
const SendOfferPage = lazy(() => import("./pages/admin/SendOfferPage"));
const LeadsPage = lazy(() => import("./pages/admin/LeadsPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const BiznisPromptPack2025Page = lazy(() => import("./pages/BiznisPromptPack2025Page"));
const PromptDetailPage = lazy(() => import("./pages/PromptDetailPage"));
const MojUcet = lazy(() => import("./pages/MojUcet"));
const CustomerLogin = lazy(() => import("./pages/CustomerLogin"));
const OrdersPage = lazy(() => import("./pages/admin/OrdersPage"));
const EntitlementsPage = lazy(() => import("./pages/admin/EntitlementsPage"));

const queryClient = new QueryClient();

// Google Analytics page tracking for SPA
function GoogleAnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Send pageview to Google Analytics on route change
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-C966R7QFS3', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
}

// Declare gtag on window
declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: Record<string, unknown>) => void;
    dataLayer: unknown[];
  }
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <GoogleAnalyticsTracker />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/ai-mastery-program" element={<AIMasteryProgram />} />
              <Route path="/gdpr" element={<GDPR />} />
              <Route path="/vop" element={<VOP />} />
              <Route path="/materialy" element={<Materialy />} />
              <Route path="/zadarmo" element={<ZadarmoPage />} />
              <Route path="/ai-test" element={<AITest />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/prihlasenie" element={<AdminLogin />} />
              <Route path="/admin/crm" element={<CRMDashboard />}>
                <Route index element={<DashboardHome />} />
                <Route path="pipeline" element={<PipelineView />} />
                <Route path="organizacie" element={<OrganizationsPage />} />
                <Route path="kontakty" element={<ContactsPage />} />
                <Route path="prilezitosti" element={<OpportunitiesPage />} />
                <Route path="ulohy" element={<TasksPage />} />
                <Route path="odoslat-ponuku" element={<SendOfferPage />} />
                <Route path="leady" element={<LeadsPage />} />
                <Route path="analytika" element={<AnalyticsPage />} />
                <Route path="objednavky" element={<OrdersPage />} />
                <Route path="pristupy" element={<EntitlementsPage />} />
              </Route>
              <Route path="/biznis-promptpack-2026" element={<BiznisPromptPack2025Page />} />
              <Route path="/biznis-promptpack-2026/:itemId" element={<PromptDetailPage />} />
              {/* Redirect old URLs */}
              <Route path="/biznis-promptpack-2025" element={<BiznisPromptPack2025Page />} />
              <Route path="/biznis-promptpack-2025/:itemId" element={<PromptDetailPage />} />
              <Route path="/moj-ucet" element={<MojUcet />} />
              <Route path="/prihlasenie" element={<CustomerLogin />} />
              <Route path="*" element={<Home />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </Suspense>
          <CookieBanner />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
